import React, { memo, useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  SESSION_AUTHENTICATED,
  clearUser,
  selectSessionStatus,
} from "../../store/slices/user";
import { selectCurrentProject } from "../../store/slices/projects";
import axios from "axios";
import "./main-layout-v2.css";
import { fetchSettings } from "../../store/slices/settings";
import { bootstrapProjects } from "../../store/thunks/bootstrapProjects";
import { SidebarContext } from "../sidebar/sidebar-context";
import useSessionInterceptor from "../../common/utils/use-session-interceptor";

export const MainLayoutV2 = memo(({ children, sider }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Tracks whether a <Sidebar /> is actually mounted as a child. Pages like the
  // dashboard render no sidebar, so the header hamburger has nothing to toggle —
  // Sidebar registers itself here on mount so Header can hide the hamburger when
  // there's no sub-menu to show.
  const [hasSider, setHasSider] = useState(false);
  const dispatch = useDispatch();
  const location = useLocation();

  const currentProject = useSelector(selectCurrentProject);

  /**
   * LF360-1970: whether there is a session, rather than whether the page is
   * holding a token. The cookie is HttpOnly, so `localStorage` no longer knows
   * — and `currentUser.token`, which this used to render on, is never set now.
   */
  const signedIn = useSelector(selectSessionStatus) === SESSION_AUTHENTICATED;

  const kickUser = useCallback(() => {
    dispatch(clearUser());
    // Any token from before the cookie release, so a session the server has
    // already refused is not presented again on the next request.
    localStorage.removeItem("user_token");
    localStorage.removeItem("current_project_id");
  }, [dispatch]);

  useEffect(() => {
    const fallbackTitle = 'LifeFit360';

    if (!currentProject || !currentProject.code) {
      document.title = fallbackTitle;
      return;
    }

    const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);

    const pathParts = location.pathname.split('/').filter(p => p && p !== currentProject.code);
    const titleParts = pathParts.map(p => {
      return p.split('-').map(capitalize).join(' ');
    });

    const pageTitle = titleParts.join(' - ');

    if (pageTitle) {
      document.title = `${currentProject.code} - ${pageTitle}`;
    } else {
      document.title = `${currentProject.code} - Dashboard`; // Default page
    }
  }, [currentProject, location.pathname]);

  // LF360-1970: shared with main-layout, so the rule cannot drift between them.
  useSessionInterceptor(kickUser);

  useEffect(() => {
    const pid = currentProject?.id || null;
    if (pid) localStorage.setItem("current_project_id", pid);
    else localStorage.removeItem("current_project_id");

    const reqId = axios.interceptors.request.use((config) => {

      config.headers = config.headers || {};

      return config;
    });

    return () => axios.interceptors.request.eject(reqId);
  }, [currentProject?.id]);

  // The user itself is read once, in `useSession`; what is left for the layout
  // is the projects it needs to render.
  useEffect(() => {
    if (!signedIn) return;

    dispatch(bootstrapProjects());
  }, [signedIn, dispatch]);

  useEffect(() => {
    if (!signedIn || !currentProject?.id) return;
    dispatch(fetchSettings());
  }, [dispatch, signedIn, currentProject?.id]);

  if (!signedIn || !currentProject?.id) {
    return null;
  }

  return (
    <SidebarContext.Provider value={{ isOpen: sidebarOpen, setIsOpen: setSidebarOpen, hasSider, setHasSider }}>
      <div className="lf-main-layout" style={{ minHeight: "100vh", overflow: "hidden" }}>
        {children}
        {sider}
      </div>
    </SidebarContext.Provider>
  );
});
